"use strict";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const core_1 = require("@apollo/client/core");
const compact_1 = __importDefault(require("lodash/compact"));
const every_1 = __importDefault(require("lodash/every"));
const pick_1 = __importDefault(require("lodash/pick"));
const take_1 = __importDefault(require("lodash/take"));
const isNil_1 = __importDefault(require("lodash/isNil"));
const isFunction_1 = __importDefault(require("lodash/isFunction"));
const orderBy_1 = __importDefault(require("lodash/orderBy"));
const InvalidationPolicyManager_1 = __importDefault(require("../policies/InvalidationPolicyManager"));
const entity_store_1 = require("../entity-store");
const helpers_1 = require("../helpers");
const CacheResultProcessor_1 = require("./CacheResultProcessor");
const types_1 = require("../policies/types");
const utils_1 = require("./utils");
const ReactiveVarsCache_1 = require("./ReactiveVarsCache");
/**
 * Extension of Apollo in-memory cache which adds support for cache policies.
 */
// @ts-ignore: Private API overloads
class InvalidationPolicyCache extends core_1.InMemoryCache {
    constructor(config = {}) {
        const { invalidationPolicies = {}, enableCollections = false } = config, inMemoryCacheConfig = __rest(config, ["invalidationPolicies", "enableCollections"]);
        super(inMemoryCacheConfig);
        this.pendingCollectionUpdates = {};
        this.enableCollections = enableCollections;
        this.invalidationPolicies = invalidationPolicies;
        this.isBroadcasting = false;
        this.isInitialized = true;
        this.reactiveVarsCache = (0, ReactiveVarsCache_1.initReactiveVarsCache)(this);
        // Once the InMemoryCache has called `init()` in the super constructor, we initialize
        // the InvalidationPolicyCache objects.
        this.reinitialize();
    }
    // Whenever the InMemoryCache reinitializes the entityStore by calling `init()` again,
    // we must also reinitialize all of the InvalidationPolicyCache objects.
    reinitialize() {
        // @ts-ignore Data is a private API
        this.entityStoreRoot = this.data;
        this.entityTypeMap = new entity_store_1.EntityTypeMap();
        this.entityStoreWatcher = new entity_store_1.EntityStoreWatcher({
            entityStore: this.entityStoreRoot,
            entityTypeMap: this.entityTypeMap,
            policies: this.policies,
            reactiveVarsCache: this.reactiveVarsCache,
            updateCollectionField: this.updateCollectionField.bind(this),
        });
        this.invalidationPolicyManager = new InvalidationPolicyManager_1.default({
            policies: this.invalidationPolicies,
            entityTypeMap: this.entityTypeMap,
            cacheOperations: {
                evict: (...args) => this.evict(...args),
                modify: (...args) => this.modify(...args),
                readField: (...args) => this.readField(...args),
            },
        });
        this.cacheResultProcessor = new CacheResultProcessor_1.CacheResultProcessor({
            invalidationPolicyManager: this.invalidationPolicyManager,
            // @ts-ignore This field is assigned in the parent constructor
            entityTypeMap: this.entityTypeMap,
            cache: this,
        });
        this.reactiveVarsCache.reset();
    }
    // @ts-ignore private API
    init() {
        // @ts-ignore private API
        super.init();
        // After init is called, the entity store has been reset so we must
        // reinitialize all invalidation policy objects.
        if (this.isInitialized) {
            this.reinitialize();
        }
    }
    readField(fieldNameOrOptions, from) {
        if (!fieldNameOrOptions) {
            return;
        }
        const options = typeof fieldNameOrOptions === "string"
            ? {
                fieldName: fieldNameOrOptions,
                from,
            }
            : fieldNameOrOptions;
        if (void 0 === options.from) {
            options.from = { __ref: 'ROOT_QUERY' };
        }
        return this.policies.readField(options, {
            store: this.entityStoreRoot,
        });
    }
    broadcastWatches() {
        this.isBroadcasting = true;
        super.broadcastWatches();
        this.isBroadcasting = false;
    }
    get _hasPendingCollectionUpdates() {
        return Object.keys(this.pendingCollectionUpdates).length > 0;
    }
    // Determines whether the cache's data reference is set to the root store. If not, then there is an ongoing optimistic transaction
    // being applied to a new layer.
    isOperatingOnRootData() {
        // @ts-ignore
        return this.data === this.entityStoreRoot;
    }
    modify(options) {
        var _a;
        const modifyResult = super.modify(options);
        if (!this.invalidationPolicyManager.isPolicyEventActive(types_1.InvalidationPolicyEvent.Write) ||
            !modifyResult) {
            return modifyResult;
        }
        const { id = "ROOT_QUERY", fields } = options;
        if ((0, helpers_1.isQuery)(id)) {
            Object.keys(fields).forEach((storeFieldName) => {
                var _a;
                const fieldName = (0, helpers_1.fieldNameFromStoreName)(storeFieldName);
                const typename = (_a = this.entityTypeMap.readEntityById((0, helpers_1.makeEntityId)(id, fieldName))) === null || _a === void 0 ? void 0 : _a.typename;
                if (!typename) {
                    return;
                }
                this.invalidationPolicyManager.runWritePolicy(typename, {
                    parent: {
                        id,
                        fieldName,
                        storeFieldName,
                        ref: (0, core_1.makeReference)(id),
                    },
                });
            });
        }
        else {
            const typename = (_a = this.entityTypeMap.readEntityById(id)) === null || _a === void 0 ? void 0 : _a.typename;
            if (!typename) {
                return modifyResult;
            }
            this.invalidationPolicyManager.runWritePolicy(typename, {
                parent: {
                    id,
                    ref: (0, core_1.makeReference)(id),
                },
            });
        }
        if (options.broadcast) {
            this.broadcastWatches();
        }
        return modifyResult;
    }
    write(options) {
        const writeResult = super.write(options);
        // Do not trigger a write policy if the current write is being applied to an optimistic data layer since
        // the policy will later be applied when the server data response is received.
        if ((!this.invalidationPolicyManager.isPolicyEventActive(types_1.InvalidationPolicyEvent.Write) &&
            !this.invalidationPolicyManager.isPolicyEventActive(types_1.InvalidationPolicyEvent.Read)) ||
            !this.isOperatingOnRootData()) {
            return writeResult;
        }
        this.cacheResultProcessor.processWriteResult(options);
        if (options.broadcast) {
            this.broadcastWatches();
        }
        return writeResult;
    }
    // Evicts all entities of the given type matching the filter criteria. Returns a list of evicted entities
    // by reference.
    evictWhere(filters) {
        const { __typename, filter } = filters;
        const references = this.readReferenceWhere({
            __typename,
            filter,
        });
        references.forEach((ref) => this.evict({ id: ref.__ref, broadcast: false }));
        this.broadcastWatches();
        return references;
    }
    evict(options) {
        var _a;
        const { fieldName, args } = options;
        let { id } = options;
        if (!id) {
            if (Object.prototype.hasOwnProperty.call(options, "id")) {
                return false;
            }
            id = "ROOT_QUERY";
        }
        if (this.invalidationPolicyManager.isPolicyEventActive(types_1.InvalidationPolicyEvent.Evict)) {
            const { typename } = (_a = this.entityTypeMap.readEntityById((0, helpers_1.makeEntityId)(id, fieldName))) !== null && _a !== void 0 ? _a : {};
            if (typename) {
                const storeFieldName = (0, helpers_1.isQuery)(id) && fieldName
                    ? this.policies.getStoreFieldName({
                        typename,
                        fieldName,
                        args,
                    })
                    : undefined;
                this.invalidationPolicyManager.runEvictPolicy(typename, {
                    parent: {
                        id,
                        fieldName,
                        storeFieldName,
                        variables: args,
                        ref: (0, core_1.makeReference)(id),
                    },
                });
            }
        }
        return super.evict(options);
    }
    _updatePendingCollection(collectionEntityId) {
        const updatedReferences = this.pendingCollectionUpdates[collectionEntityId];
        if (!updatedReferences) {
            return;
        }
        this.modify({
            broadcast: false,
            id: collectionEntityId,
            fields: {
                data: (existing, { canRead }) => {
                    const existingReferences = existing;
                    let hasDuplicateRef = false;
                    const existingReferencesById = existingReferences.reduce((acc, ref) => {
                        const { __ref } = ref;
                        if (!hasDuplicateRef && acc[__ref]) {
                            hasDuplicateRef = true;
                        }
                        acc[__ref] = ref;
                        return acc;
                    }, {});
                    const newReferences = Object.values(updatedReferences).filter((ref) => !existingReferencesById[ref.__ref] && canRead(ref));
                    if (!hasDuplicateRef && newReferences.length === 0) {
                        return existing;
                    }
                    return [
                        ...Object.values(existingReferencesById),
                        ...newReferences,
                    ];
                }
            }
        });
        delete this.pendingCollectionUpdates[collectionEntityId];
    }
    _updatePendingCollections() {
        if (!this._hasPendingCollectionUpdates) {
            return;
        }
        Object.keys(this.pendingCollectionUpdates).forEach((collectionEntityId) => {
            this._updatePendingCollection(collectionEntityId);
        });
        this.broadcastWatches();
    }
    updateCollectionField(typename, dataId) {
        // Since colletion support is still experimental, only record entities in collections if enabled
        if (!this.enableCollections) {
            return;
        }
        const collectionEntityId = (0, utils_1.collectionEntityIdForType)(typename);
        const collectionFieldExists = !!this.readField('id', (0, core_1.makeReference)(collectionEntityId));
        // If the collection field for the type does not exist in the cache, then initialize it as
        // an empty array.
        if (!collectionFieldExists) {
            this.writeFragment({
                id: collectionEntityId,
                fragment: (0, core_1.gql) `
          fragment InitializeCollectionEntity on CacheExtensionsCollectionEntity {
            data
            id
          }
        `,
                data: {
                    __typename: utils_1.cacheExtensionsCollectionTypename,
                    id: typename,
                    data: [],
                },
            });
        }
        // If the entity does not already exist in the cache, add it to the collection field policy for its type
        if (!this.entityTypeMap.readEntityById(dataId)) {
            if (!this._hasPendingCollectionUpdates) {
                // Collection updates are scheduled on a separate tick so that they can be aggregated across the current tick and minimize the number
                // of collection modifications.
                setTimeout(() => this._updatePendingCollections(), 0);
            }
            if (!this.pendingCollectionUpdates[collectionEntityId]) {
                this.pendingCollectionUpdates[collectionEntityId] = {};
            }
            if (!this.pendingCollectionUpdates[collectionEntityId][dataId]) {
                this.pendingCollectionUpdates[collectionEntityId][dataId] = (0, core_1.makeReference)(dataId);
            }
        }
    }
    // Returns all expired entities whose cache time exceeds their type's timeToLive or as a fallback
    // the global timeToLive if specified. Evicts the expired entities by default, with an option to only report
    // them.
    _expire(reportOnly = false) {
        const { entitiesById } = this.entityTypeMap.extract();
        const expiredEntityIds = [];
        Object.keys(entitiesById).forEach((entityId) => {
            const entity = entitiesById[entityId];
            const { storeFieldNames, dataId, fieldName, typename } = entity;
            if ((0, helpers_1.isQuery)(dataId) && storeFieldNames) {
                Object.keys(storeFieldNames.entries).forEach((storeFieldName) => {
                    const isExpired = this.invalidationPolicyManager.runReadPolicy({
                        typename,
                        dataId,
                        fieldName,
                        storeFieldName,
                        reportOnly,
                    });
                    if (isExpired) {
                        expiredEntityIds.push((0, helpers_1.makeEntityId)(dataId, storeFieldName));
                    }
                });
            }
            else {
                const isExpired = this.invalidationPolicyManager.runReadPolicy({
                    typename,
                    dataId,
                    fieldName,
                    reportOnly,
                });
                if (isExpired) {
                    expiredEntityIds.push((0, helpers_1.makeEntityId)(dataId));
                }
            }
        });
        if (expiredEntityIds.length > 0) {
            this.broadcastWatches();
        }
        return expiredEntityIds;
    }
    // Expires all entities still present in the cache that have exceeded their timeToLive. By default entities are evicted
    // lazily on read if their entity is expired. Use this expire API to eagerly remove expired entities.
    expire() {
        return this._expire(false);
    }
    // Returns all expired entities still present in the cache.
    expiredEntities() {
        return this._expire(true);
    }
    // Activates the provided policy events (on read, on write, on evict) or by default all policy events.
    activatePolicyEvents(...policyEvents) {
        if (policyEvents.length > 0) {
            this.invalidationPolicyManager.activatePolicies(...policyEvents);
        }
        else {
            this.invalidationPolicyManager.activatePolicies(types_1.InvalidationPolicyEvent.Read, types_1.InvalidationPolicyEvent.Write, types_1.InvalidationPolicyEvent.Evict);
        }
    }
    // Deactivates the provided policy events (on read, on write, on evict) or by default all policy events.
    deactivatePolicyEvents(...policyEvents) {
        if (policyEvents.length > 0) {
            this.invalidationPolicyManager.deactivatePolicies(...policyEvents);
        }
        else {
            this.invalidationPolicyManager.deactivatePolicies(types_1.InvalidationPolicyEvent.Read, types_1.InvalidationPolicyEvent.Write, types_1.InvalidationPolicyEvent.Evict);
        }
    }
    // Returns the policy events that are currently active.
    activePolicyEvents() {
        return [
            types_1.InvalidationPolicyEvent.Read,
            types_1.InvalidationPolicyEvent.Write,
            types_1.InvalidationPolicyEvent.Evict
        ].filter(policyEvent => this.invalidationPolicyManager.isPolicyEventActive(policyEvent));
    }
    read(options) {
        const result = super.read(options);
        if (!this.invalidationPolicyManager.isPolicyEventActive(types_1.InvalidationPolicyEvent.Read)) {
            return result;
        }
        const processedResult = (0, helpers_1.maybeDeepClone)(result);
        const processedResultStatus = this.cacheResultProcessor.processReadResult(processedResult, options);
        if (processedResultStatus === CacheResultProcessor_1.ReadResultStatus.Complete) {
            return result;
        }
        this.broadcastWatches();
        return processedResultStatus === CacheResultProcessor_1.ReadResultStatus.Evicted
            ? null
            : processedResult;
    }
    diff(options) {
        const cacheDiff = super.diff(options);
        // Diff calls made by `broadcastWatches` should not trigger the read policy
        // as these are internal reads not reflective of client action and can lead to recursive recomputation of cached data which is an error.
        // Instead, diffs will trigger the read policies for client-based reads like `readCache` invocations from watched queries outside
        // the scope of broadcasts.
        if (!this.invalidationPolicyManager.isPolicyEventActive(types_1.InvalidationPolicyEvent.Read) ||
            this.isBroadcasting) {
            return cacheDiff;
        }
        const { result } = cacheDiff;
        const processedResult = (0, helpers_1.maybeDeepClone)(result);
        const processedResultStatus = this.cacheResultProcessor.processReadResult(processedResult, options);
        if (processedResultStatus === CacheResultProcessor_1.ReadResultStatus.Complete) {
            return cacheDiff;
        }
        this.broadcastWatches();
        cacheDiff.complete = false;
        cacheDiff.result =
            processedResultStatus === CacheResultProcessor_1.ReadResultStatus.Evicted
                ? undefined
                : processedResult;
        return cacheDiff;
    }
    extract(optimistic = false, withInvalidation = true) {
        if (this._hasPendingCollectionUpdates) {
            this._updatePendingCollections();
        }
        const extractedCache = super.extract(optimistic);
        if (withInvalidation) {
            // The entitiesById are sufficient alone for reconstructing the type map, so to
            // minimize payload size only inject the entitiesById object into the extracted cache
            extractedCache.invalidation = (0, pick_1.default)(this.entityTypeMap.extract(), "entitiesById");
        }
        return extractedCache;
    }
    // Supports reading a collection of entities by type from the cache and filtering them by the given fields. Returns
    // a list of the dereferenced matching entities from the cache based on the given fragment.
    readFragmentWhere(options) {
        const { fragment, filter, limit, orderBy } = options, restOptions = __rest(options, ["fragment", "filter", "limit", "orderBy"]);
        const fragmentDefinition = fragment.definitions[0];
        const __typename = fragmentDefinition.typeCondition.name.value;
        const matchingRefs = this.readReferenceWhere({
            __typename,
            filter,
            limit,
            orderBy,
        });
        const matchingFragments = matchingRefs.map(ref => this.readFragment(Object.assign(Object.assign({}, restOptions), { fragment, id: ref.__ref })));
        return (0, compact_1.default)(matchingFragments);
    }
    // Supports reading a collection of references by type from the cache and filtering them by the given fields. Returns a
    // list of the matching references.
    readReferenceWhere(options) {
        const { __typename, filter, limit, orderBy } = options;
        const collectionEntityId = (0, utils_1.collectionEntityIdForType)(__typename);
        // If a stale collection is accessed while it has a pending update, then eagerly update it before the read.
        if (this.pendingCollectionUpdates[collectionEntityId]) {
            this._updatePendingCollection(collectionEntityId);
        }
        let references = this.readField('data', (0, core_1.makeReference)(collectionEntityId));
        if (!references) {
            return [];
        }
        const readField = this.readField.bind(this);
        if (filter) {
            references = references.filter(ref => {
                if ((0, isFunction_1.default)(filter)) {
                    return filter(ref, readField);
                }
                const entityFilterResults = Object.keys(filter).map(filterField => {
                    // @ts-ignore
                    const filterValue = filter[filterField];
                    const entityValueForFilter = this.readField(filterField, ref);
                    return filterValue === entityValueForFilter;
                });
                return (0, every_1.default)(entityFilterResults, Boolean);
            });
        }
        if (orderBy) {
            const { field, descending } = orderBy;
            references = (0, orderBy_1.default)(references, (ref) => readField(field, ref), [descending ? 'desc' : 'asc']);
        }
        if (!(0, isNil_1.default)(limit)) {
            return (0, take_1.default)(references, limit);
        }
        return references;
    }
    writeFragmentWhere(options) {
        const { update } = options, readOptions = __rest(options, ["update"]);
        this.readFragmentWhere(readOptions).forEach((entity) => {
            this.writeFragment({
                // @ts-ignore This
                id: this.identify(entity),
                fragment: options.fragment,
                data: update(entity),
                broadcast: false,
            });
        });
        this.broadcastWatches();
    }
}
exports.default = InvalidationPolicyCache;
//# sourceMappingURL=InvalidationPolicyCache.js.map